import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Search, X, Edit3, Trash2, CheckCircle2, IndianRupee, Percent, Wallet, Building2, Save, MoreVertical } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import Portal from "./Portal";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const StatusBadge = ({ active }) => (
  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${active ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`}>
    {active ? "Active" : "Inactive"}
  </span>
);

const emptyAgent = () => ({ name: "", contact_person: "", email: "", phone: "", mobile: "", short_code: "",
  registration_no: "", tax_id: "", opening_balance: 0, credit_limit: 0,
  commission_plan: "percent", commission_value: 10, discount_pct: 0,
  kind: "offline", address: "", zip: "", country: "India", state: "", city: "", active: true });

export default function TravelAgentsTab({ pid, propertyName }) {
  const [agents, setAgents] = useState(null);
  const [q, setQ] = useState("");
  const [drawer, setDrawer] = useState(null); // "new" | "edit" | "ledger"
  const [form, setForm] = useState(emptyAgent());
  const [ledger, setLedger] = useState(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);

  const load = useCallback(() => {
    setAgents(null);
    Promise.all([
      api.get(`/pms/properties/${pid}/travel-agents`),
      api.get(`/pms/properties/${pid}/travel-agents-summary`),
    ]).then(([a, s]) => { setAgents(a.data.agents); setSummary(s.data); })
      .catch((e) => toast.error(errMsg(e)));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const filtered = (agents || []).filter((a) => !q.trim() || (a.name + a.email + a.phone + a.short_code).toLowerCase().includes(q.toLowerCase()));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Agent name is required");
    setBusy(true);
    try {
      if (drawer === "new") await api.post(`/pms/properties/${pid}/travel-agents`, form);
      else if (drawer === "edit") await api.patch(`/pms/properties/${pid}/travel-agents/${form.id}`, form);
      toast.success(`Travel agent ${drawer === "new" ? "created" : "updated"}`);
      setDrawer(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const openLedger = async (a) => {
    setDrawer("ledger"); setLedger(null);
    try {
      const { data } = await api.get(`/pms/properties/${pid}/travel-agents/${a.id}/ledger`);
      setLedger(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const markPaid = async (a) => {
    if (!window.confirm(`Mark ₹${fmt(a.balance)} paid to ${a.name}? This settles all pending & earned commissions to zero.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/pms/properties/${pid}/travel-agents/${a.id}/mark-paid`);
      toast.success(`${data.cleared} commission row${data.cleared === 1 ? "" : "s"} settled — ${a.name} balance is now ₹0.00`);
      load(); if (drawer === "ledger" && ledger?.agent?.id === a.id) openLedger(a);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete '${a.name}'? Their commission history will also be removed.`)) return;
    try { await api.delete(`/pms/properties/${pid}/travel-agents/${a.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const logCommission = async (row) => {
    if (!ledger) return;
    setBusy(true);
    try {
      await api.post(`/pms/properties/${pid}/travel-agents/${ledger.agent.id}/log-commission`, row);
      toast.success(`Commission logged`); openLedger(ledger.agent); load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (!agents) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-300" size={28} /></div>;

  return (
    <div className="reveal space-y-5">
      {/* Summary strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {[
          { l: "Agents", v: summary?.agent_count ?? 0, i: Building2, tone: "bg-white" },
          { l: "Payable now", v: `₹${fmt(summary?.total_balance)}`, i: Wallet, tone: "bg-amber-50 border-amber-200 text-amber-900" },
          { l: "Lifetime earned", v: `₹${fmt(summary?.lifetime_earned)}`, i: IndianRupee, tone: "bg-white" },
          { l: "Lifetime paid", v: `₹${fmt(summary?.lifetime_paid)}`, i: CheckCircle2, tone: "bg-emerald-50 border-emerald-200 text-emerald-900" },
        ].map((c, i) => (
          <div key={i} data-testid={`ta-stat-${i}`} className={`rounded-2xl border border-slate-200 p-4 ${c.tone}`}>
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider opacity-70"><c.i size={12} /> {c.l}</div>
            <p className="font-display font-black text-xl mt-1">{c.v}</p>
          </div>
        ))}
      </div>
      {summary && (summary.online_balance > 0 || summary.offline_balance > 0) && (
        <p className="text-[11px] text-slate-500">Payable split — online agents (Airbnb / Booking / MMT etc.): <b>₹{fmt(summary.online_balance)}</b> · offline agents: <b>₹{fmt(summary.offline_balance)}</b></p>
      )}

      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-slate-900 text-lg">Travel Agent Database</h2>
          <p className="text-xs text-slate-500 mt-1">{propertyName} · one consolidated view of every online + offline agent commission.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="ta-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Quick search by name, email, contact…"
              className="pl-8 pr-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40 w-72" />
          </div>
          <button data-testid="ta-add-button" onClick={() => { setForm(emptyAgent()); setDrawer("new"); }}
            className="btn-brand rounded-lg px-4 py-2 text-xs font-semibold flex items-center gap-1.5"><Plus size={13} /> Add Travel Agent</button>
        </div>
      </div>

      {/* Table (≥lg) */}
      <div className="hidden lg:block bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              <th className="px-4 py-3">Agent Name</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Commission</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Balance (₹)</th>
              <th className="px-4 py-3 w-8" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="py-12 text-center text-slate-400 text-sm">No travel agents yet — add your first one to start tracking commissions.</td></tr>
            )}
            {filtered.map((a) => (
              <tr key={a.id} data-testid={`ta-row-${a.name}`} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => openLedger(a)}>
                <td className="px-4 py-3 font-semibold text-slate-900">{a.name}
                  {a.kind === "online" && <span className="ml-2 text-[9px] font-bold uppercase text-sky-700 bg-sky-50 border border-sky-200 px-1.5 py-0.5 rounded">Online · {a.channel}</span>}
                </td>
                <td className="px-4 py-3 text-slate-600">{a.contact_person || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{a.phone || a.mobile || "—"}</td>
                <td className="px-4 py-3 text-slate-600 truncate max-w-[240px]">{a.email || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{a.commission_plan === "percent" ? `${a.commission_value}%` : `₹${a.commission_value} flat`}</td>
                <td className="px-4 py-3"><StatusBadge active={a.active} /></td>
                <td className={`px-4 py-3 text-right font-mono font-bold ${a.balance > 0 ? "text-amber-700" : "text-slate-400"}`}>{fmt(a.balance)}</td>
                <td className="px-4 py-3">
                  <details className="relative" onClick={(e) => e.stopPropagation()}>
                    <summary className="list-none cursor-pointer text-slate-400 hover:text-slate-700"><MoreVertical size={14} /></summary>
                    <div className="absolute right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-10 w-40 py-1">
                      <button onClick={() => { setForm({ ...a }); setDrawer("edit"); }} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2"><Edit3 size={12} /> Edit</button>
                      {a.balance > 0 && <button data-testid={`ta-mark-paid-${a.name}`} onClick={() => markPaid(a)} className="w-full text-left px-3 py-2 text-xs hover:bg-emerald-50 text-emerald-700 flex items-center gap-2"><CheckCircle2 size={12} /> Mark paid (₹{fmt(a.balance)})</button>}
                      <button onClick={() => remove(a)} className="w-full text-left px-3 py-2 text-xs hover:bg-rose-50 text-rose-700 flex items-center gap-2"><Trash2 size={12} /> Delete</button>
                    </div>
                  </details>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Card list (<lg) — every agent card is tap-large; overflow menu becomes inline buttons */}
      <div className="lg:hidden space-y-2">
        {filtered.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 py-8 px-4 text-center text-slate-400 text-sm">
            No travel agents yet — add your first one to start tracking commissions.
          </div>
        )}
        {filtered.map((a) => (
          <div key={a.id} data-testid={`ta-card-${a.name}`}
            className="bg-white rounded-2xl border border-slate-200 p-4 active:bg-slate-50 transition-colors">
            <button onClick={() => openLedger(a)} className="w-full text-left">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 truncate">{a.name}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {a.kind === "online" && <span className="text-[9px] font-bold uppercase text-sky-700 bg-sky-50 border border-sky-200 px-1.5 py-0.5 rounded">Online · {a.channel}</span>}
                    <StatusBadge active={a.active} />
                    <span className="text-[10px] text-slate-500 font-mono">{a.commission_plan === "percent" ? `${a.commission_value}%` : `₹${a.commission_value} flat`}</span>
                  </div>
                  {(a.contact_person || a.phone || a.mobile) && <p className="text-[11px] text-slate-500 mt-1 truncate">{a.contact_person || "—"} · {a.phone || a.mobile || "—"}</p>}
                </div>
                <div className={`text-right shrink-0 font-mono font-bold ${a.balance > 0 ? "text-amber-700" : "text-slate-400"}`}>
                  ₹{fmt(a.balance)}
                  <div className="text-[10px] text-slate-400 font-normal">balance</div>
                </div>
              </div>
            </button>
            <div className="mt-3 flex gap-2 flex-wrap">
              <button onClick={() => { setForm({ ...a }); setDrawer("edit"); }}
                className="text-[11px] px-3 py-1.5 rounded-full border border-slate-200 text-slate-700 flex items-center gap-1 hover:border-slate-400"><Edit3 size={11} /> Edit</button>
              {a.balance > 0 && (
                <button data-testid={`ta-mark-paid-m-${a.name}`} onClick={() => markPaid(a)}
                  className="text-[11px] px-3 py-1.5 rounded-full border border-emerald-200 text-emerald-700 bg-emerald-50 flex items-center gap-1"><CheckCircle2 size={11} /> Mark paid</button>
              )}
              <button onClick={() => remove(a)}
                className="text-[11px] px-3 py-1.5 rounded-full border border-rose-200 text-rose-700 flex items-center gap-1 ml-auto"><Trash2 size={11} /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Add / Edit drawer */}
      {(drawer === "new" || drawer === "edit") && (
      <Portal>
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[80] grid place-items-center p-4 sm:p-6 overflow-y-auto" onClick={() => setDrawer(null)}>
          <div data-testid="ta-drawer" className="bg-white w-[96vw] min-h-[92vh] max-h-[92vh] max-w-none rounded-2xl shadow-2xl p-8 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-bold text-lg text-slate-900">{drawer === "new" ? "Add Travel Agent" : `Edit ${form.name}`}</h3>
              <button data-testid="ta-drawer-close" onClick={() => setDrawer(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <Row2>
                <Field label="Agent Name*"><input data-testid="ta-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} /></Field>
                <Field label="Contact Person"><input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className={inp} /></Field>
              </Row2>
              <Row2>
                <Field label="Email"><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} /></Field>
                <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inp} /></Field>
              </Row2>
              <Row2>
                <Field label="Mobile"><input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className={inp} /></Field>
                <Field label="Short Code"><input value={form.short_code} onChange={(e) => setForm({ ...form, short_code: e.target.value })} className={inp} /></Field>
              </Row2>
              <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400 pt-3">Account Details</p>
              <Row2>
                <Field label="Registration No"><input value={form.registration_no} onChange={(e) => setForm({ ...form, registration_no: e.target.value })} className={inp} /></Field>
                <Field label="Tax ID / GSTIN"><input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} className={inp} /></Field>
              </Row2>
              <Row2>
                <Field label="Opening Balance (₹)"><input type="number" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: parseFloat(e.target.value) || 0 })} className={inp} /></Field>
                <Field label="Credit Limit (₹)"><input type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: parseFloat(e.target.value) || 0 })} className={inp} /></Field>
              </Row2>
              <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400 pt-3">Commission</p>
              <Row2>
                <Field label="Plan">
                  <select data-testid="ta-form-plan" value={form.commission_plan} onChange={(e) => setForm({ ...form, commission_plan: e.target.value })} className={inp}>
                    <option value="percent">Percent of revenue</option>
                    <option value="flat">Flat per booking</option>
                  </select>
                </Field>
                <Field label={form.commission_plan === "percent" ? "Value (%)" : "Value (₹)"}>
                  <div className="relative">
                    <input data-testid="ta-form-value" type="number" value={form.commission_value} onChange={(e) => setForm({ ...form, commission_value: parseFloat(e.target.value) || 0 })} className={inp + " pr-8"} />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{form.commission_plan === "percent" ? <Percent size={12} /> : <IndianRupee size={12} />}</span>
                  </div>
                </Field>
              </Row2>
              <Field label="Discount on Standard Rates (%)"><input type="number" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: parseFloat(e.target.value) || 0 })} className={inp} /></Field>

              <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400 pt-3">Address</p>
              <Field label="Address"><textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inp + " h-20"} /></Field>
              <Row2>
                <Field label="Zip"><input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className={inp} /></Field>
                <Field label="City"><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inp} /></Field>
              </Row2>
              <Row2>
                <Field label="State"><input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={inp} /></Field>
                <Field label="Country"><input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={inp} /></Field>
              </Row2>
              <label className="flex items-center gap-2 text-xs text-slate-600 pt-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setDrawer(null)} className="text-xs px-4 py-2 rounded-full border border-slate-200 hover:border-slate-400">Cancel</button>
              <button data-testid="ta-drawer-save" onClick={save} disabled={busy} className="btn-brand rounded-full px-5 py-2 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />} Save
              </button>
            </div>
          </div>
        </div>
      </Portal>
      )}

      {/* Ledger drawer */}
      {drawer === "ledger" && (
        <LedgerDrawer
          pid={pid}
          ledger={ledger}
          busy={busy}
          onClose={() => { setDrawer(null); setLedger(null); }}
          onLog={logCommission}
          onMarkPaid={() => ledger?.agent && markPaid(ledger.agent)}
        />
      )}
    </div>
  );
}

const inp = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40";
const Row2 = ({ children }) => <div className="grid grid-cols-2 gap-3">{children}</div>;
const Field = ({ label, children }) => (
  <label className="block text-xs">
    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const LedgerDrawer = ({ pid, ledger, busy, onClose, onLog, onMarkPaid }) => {
  const [row, setRow] = useState({ guest: "", revenue: "", booking_id: "" });
  if (!ledger) return (
    <Portal>
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[80] grid place-items-center p-4 sm:p-6 overflow-y-auto" onClick={onClose}>
        <div className="bg-white w-full max-w-md rounded-2xl p-16 shadow-2xl flex items-center justify-center"><Loader2 className="animate-spin text-slate-300" size={28} /></div>
      </div>
    </Portal>
  );
  const { agent, rows, balance, lifetime_earned, lifetime_paid } = ledger;
  const submitLog = () => {
    const rev = parseFloat(row.revenue);
    if (!row.guest.trim() || !rev || rev <= 0) return toast.error("Enter guest name and revenue amount");
    onLog({ guest: row.guest, revenue: rev, booking_id: row.booking_id });
    setRow({ guest: "", revenue: "", booking_id: "" });
  };
  return (
    <Portal>
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[80] grid place-items-center p-4 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div data-testid="ta-ledger-drawer" className="bg-white w-[96vw] min-h-[92vh] max-h-[92vh] max-w-none rounded-2xl shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 bg-white border-b border-slate-200 px-7 py-5 flex items-center justify-between z-10">
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900">{agent.name}</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">{agent.commission_plan === "percent" ? `${agent.commission_value}% of revenue` : `₹${agent.commission_value} flat per booking`}{agent.kind === "online" ? ` · Online · ${agent.channel}` : " · Offline"}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </header>
        <div className="p-7 space-y-5">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3"><p className="text-[10px] uppercase font-bold text-amber-800">Balance now</p><p className="font-display font-black text-lg text-amber-900">₹{fmt(balance)}</p></div>
            <div className="rounded-xl bg-white border border-slate-200 p-3"><p className="text-[10px] uppercase font-bold text-slate-500">Lifetime earned</p><p className="font-display font-black text-lg text-slate-900">₹{fmt(lifetime_earned)}</p></div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3"><p className="text-[10px] uppercase font-bold text-emerald-800">Lifetime paid</p><p className="font-display font-black text-lg text-emerald-900">₹{fmt(lifetime_paid)}</p></div>
          </div>
          {balance > 0 && (
            <button data-testid="ta-ledger-mark-paid" onClick={onMarkPaid} disabled={busy}
              className="w-full rounded-xl border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50 py-3 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
              <CheckCircle2 size={14} /> Mark ₹{fmt(balance)} paid & reset balance to zero
            </button>
          )}
          {balance > 0 && (
            <button data-testid="ta-statement-button"
              onClick={async () => {
                try {
                  const { data: st } = await api.get(`/pms/properties/${pid}/travel-agents/${agent.id}/statement`);
                  const w = window.open("", "_blank");
                  if (!w) { toast.error("Popup blocked — allow popups to open the statement"); return; }
                  const trs = st.rows.map((r) => `<tr><td>${r.booking_date}</td><td>${r.guest}</td><td style="text-align:right">₹${fmt(r.revenue)}</td><td style="text-align:right">₹${fmt(r.amount)}</td></tr>`).join("");
                  w.document.write(`<!doctype html><html><head><title>Commission Statement · ${st.agent.name}</title><style>body{font-family:system-ui,sans-serif;max-width:820px;margin:32px auto;padding:0 24px;color:#0f172a}h1{margin:0;font-size:22px}h2{color:#4F46E5;margin:4px 0 24px;font-size:14px;font-weight:600;text-transform:uppercase;letter-spacing:.08em}table{width:100%;border-collapse:collapse;margin-top:12px}th,td{padding:10px;border-bottom:1px solid #e2e8f0;font-size:13px}th{text-align:left;background:#f8fafc;font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#64748b}.total{font-size:20px;font-weight:800;margin-top:20px;text-align:right}.hint{color:#94a3b8;font-size:11px;margin-top:24px}</style></head><body><h1>${st.property.name}</h1><h2>Commission Statement · ${st.agent.name}</h2><p><b>Plan:</b> ${st.agent.commission_plan === "percent" ? st.agent.commission_value + "%" : "₹" + st.agent.commission_value + " flat"} &nbsp; <b>Generated:</b> ${new Date(st.generated_at).toLocaleString("en-IN")}</p><table><thead><tr><th>Booking date</th><th>Guest</th><th style="text-align:right">Revenue</th><th style="text-align:right">Commission</th></tr></thead><tbody>${trs}</tbody></table><div class="total">Total payable: ₹${fmt(st.total_payable)}</div><p class="hint">Print or save as PDF from your browser (Cmd/Ctrl + P) and email to ${st.agent.email || "the agent"}.</p></body></html>`);
                  w.document.close();
                } catch (e) { toast.error(errMsg(e)); }
              }}
              className="w-full rounded-xl border border-slate-300 hover:border-[#4F46E5] hover:text-[#4F46E5] text-slate-700 py-2.5 text-xs font-semibold flex items-center justify-center gap-2">
              📄 Open printable statement (Ctrl+P → save as PDF → email to agent)
            </button>
          )}

          {/* Add offline commission */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
            <p className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-2">Log an offline commission</p>
            <div className="grid grid-cols-3 gap-2">
              <input data-testid="ta-log-guest" placeholder="Guest name" value={row.guest} onChange={(e) => setRow({ ...row, guest: e.target.value })} className={inp} />
              <input data-testid="ta-log-revenue" type="number" placeholder="Revenue (₹)" value={row.revenue} onChange={(e) => setRow({ ...row, revenue: e.target.value })} className={inp} />
              <input placeholder="Booking ID (opt)" value={row.booking_id} onChange={(e) => setRow({ ...row, booking_id: e.target.value })} className={inp} />
            </div>
            <button data-testid="ta-log-submit" onClick={submitLog} disabled={busy} className="btn-brand mt-3 rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
              {busy ? <Loader2 className="animate-spin" size={12} /> : <Plus size={12} />} Log commission
            </button>
          </div>

          {/* Ledger table */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                <tr><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">Guest</th><th className="px-4 py-2 text-right">Revenue</th><th className="px-4 py-2 text-right">Commission</th><th className="px-4 py-2 text-center">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-400 text-xs">No commissions yet.</td></tr>}
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-2 text-slate-600 text-xs font-mono">{r.booking_date}</td>
                    <td className="px-4 py-2 text-slate-800">{r.guest}</td>
                    <td className="px-4 py-2 text-right text-slate-600 font-mono">₹{fmt(r.revenue)}</td>
                    <td className="px-4 py-2 text-right font-mono font-bold text-slate-900">₹{fmt(r.amount)}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${r.status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : r.status === "earned" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    </Portal>
  );
};
